/**
 * The immersion sprint setup page: the launch checklist, the week plan, the
 * summary cards and the "Copy as next cohort" defaults. Pure, so the page and
 * the tests share one rule. Dates are plain YYYY-MM-DD calendar days.
 */

import { fmtReminderHour, isoDayOfWeekFromJsDay } from './checkin-days';
import { addDays, lastWeekNumber, mondayOf, summaryWindow } from './checkin-weeks';
import { coachAccessLastDay } from './tracker-coach';
import { TRACKER_PRESETS, TrackerPresetId } from './tracker-config';
import { AcesProgram } from './aces-program';
import { daysBetween, fmtShortDate, fmtTrackerDate } from './tracker-people';

export type ImmersionSprintSetupData = {
  course: { id: string; title: string; accessFormat: 'openaccess' | 'ondemand' | 'scheduled' };
  standups: {
    enabled: boolean;
    startDate?: string;
    endDate?: string;
    /** ISO day numbers, Monday = 1 through Sunday = 7. */
    days: number[];
    promptCount: number;
    remindersEnabled: boolean;
    reminderHour: number;
    summariesEnabled: boolean;
    sprintSummary: boolean;
    sendTo: 'address' | 'coach';
    notifyEmail?: string;
    defaultCoachName?: string;
  };
  gate: {
    enabled: boolean;
    autoRemoval: boolean;
    moduleIds: string[];
    lastEnforcedOn?: string;
    minDaysMissing: number;
  };
  tracker: {
    enabled: boolean;
    presetId?: TrackerPresetId;
    scoreboardKind: 'revenue' | 'cpl';
    channelCount: number;
    stageCount: number;
    /** 'participant' on Leads: the channels are families each participant copies. */
    channelsBy: 'course' | 'participant';
  };
  /** "Show in ACES"; absent when ACES does not follow the course. */
  acesProgram?: AcesProgram;
  /** ACES follows the core tenant's courses only; elsewhere the checklist leaves it out. */
  acesAvailable: boolean;
  modules: Array<{
    id: string;
    title: string;
    startDay?: string;
    /** The naive wall-clock release, 'YYYY-MM-DDTHH:MM'. */
    startAt?: string;
    isDraft: boolean;
    isGated: boolean;
  }>;
  /** The course's today, in the tenant's time zone. */
  today: string;
};

export type SetupFixTarget = 'checkins' | 'completion-gate' | 'modules' | 'aces';

export type ChecklistItem = {
  key:
    | 'standups'
    | 'start'
    | 'end'
    | 'questions'
    | 'lessons'
    | 'gate'
    | 'auto-removal'
    | 'last-removal'
    | 'aces';
  ready: boolean;
  label: string;
  detail?: string;
  fix?: { label: string; target: SetupFixTarget };
};

export type WeekPlanDay = {
  date: string;
  /** "Nov 2". */
  label: string;
  lessons: string[];
  standup: boolean;
  summary: boolean;
  warning: boolean;
  removal: boolean;
  holiday?: string;
  isToday: boolean;
};

export type WeekPlanWeek = {
  weekNumber: number;
  weekStart: string;
  firstLesson?: string;
  days: WeekPlanDay[];
};

export type WeekPlan = { weeks: WeekPlanWeek[]; truncated: boolean };

export type SetupFact = { label: string; value: string };

export type SetupView = {
  checklist: ChecklistItem[];
  readyCount: number;
  /** Undefined without both standup dates, or with the end before the start. */
  weekPlan?: WeekPlan;
  standupFacts: SetupFact[];
  deadlineFacts: SetupFact[];
  trackerFacts: SetupFact[];
};

export const MAX_WEEK_PLAN_WEEKS = 16;

const SHORT_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** ISO weekday, Monday = 1 through Sunday = 7. */
export function isoWeekday(dateIso: string) {
  return isoDayOfWeekFromJsDay(new Date(`${dateIso}T00:00:00Z`).getUTCDay());
}

function plural(count: number, one: string, many: string) {
  return `${count} ${count === 1 ? one : many}`;
}

/** The holiday on a day, by name: the ones a North American cohort plans around. */
export function holidayOn(dateIso: string): string | undefined {
  const month = Number(dateIso.slice(5, 7));
  const day = Number(dateIso.slice(8, 10));
  if (month === 1 && day === 1) {
    return 'New Year’s Day';
  }
  if (month === 7 && day === 1) {
    return 'Canada Day';
  }
  if (month === 7 && day === 4) {
    return 'US Independence Day';
  }
  if (month === 12 && day === 25) {
    return 'Christmas Day';
  }
  if (month === 11 && isoWeekday(dateIso) === 4 && day >= 22 && day <= 28) {
    return 'US Thanksgiving';
  }
}

/** The first Monday after a day. */
export function nextMondayAfter(dateIso: string) {
  return addDays(mondayOf(dateIso), 7);
}

/** The Mondays either side of a day that isn't one. */
export function mondayChoices(dateIso: string): [string, string] {
  const before = mondayOf(dateIso);
  return [before, addDays(before, 7)];
}

/**
 * The next cohort's name: a title ending in a bracketed month and year
 * ("[November 2026]") gets the new start's month and year; any other title
 * becomes "Copy of …".
 */
export function nextCohortTitle(title: string, startDate?: string) {
  const match = /\[([A-Za-z]+) (\d{4})\]\s*$/.exec(title);
  const isMonth = !!match && MONTH_NAMES.some((m) => m.toLowerCase() === match[1].toLowerCase());
  if (!match || !isMonth || !startDate) {
    return `Copy of ${title}`;
  }
  const month = MONTH_NAMES[Number(startDate.slice(5, 7)) - 1];
  return `${title.slice(0, match.index)}[${month} ${startDate.slice(0, 4)}]`;
}

export const MAX_NEXT_COHORT_WEEKS = 104;

export type NextCohortStart =
  | {
      ok: true;
      weeks: number;
      weekOneStart: string;
      /** The copy's standup start: the source's moved by whole weeks, so on the same weekday. */
      standupStart: string;
    }
  | { ok: false; reason: 'not-monday'; choices: [string, string] }
  | { ok: false; reason: 'not-after' | 'too-far' };

/**
 * Whether a new start date works for a next-cohort copy, and how many whole
 * weeks it moves every date. Weeks count from the Monday of the source's
 * start week, so a source starting midweek keeps its weekday.
 */
export function checkNextCohortStart(sourceStart: string, newStart: string): NextCohortStart {
  if (isoWeekday(newStart) !== 1) {
    return { ok: false, reason: 'not-monday', choices: mondayChoices(newStart) };
  }
  if (newStart <= sourceStart) {
    return { ok: false, reason: 'not-after' };
  }
  const weeks = Math.round(daysBetween(mondayOf(sourceStart), newStart) / 7);
  if (weeks > MAX_NEXT_COHORT_WEEKS) {
    return { ok: false, reason: 'too-far' };
  }
  return { ok: true, weeks, weekOneStart: newStart, standupStart: addDays(sourceStart, weeks * 7) };
}

/** "Tue, Thu, Sun". */
export function fmtStandupDays(days: number[]) {
  return [...days]
    .sort((a, b) => a - b)
    .map((d) => SHORT_DAYS[d - 1])
    .join(', ');
}

function publishedModules(data: ImmersionSprintSetupData) {
  return data.modules.filter((m) => !m.isDraft);
}

function standupItems(data: ImmersionSprintSetupData): ChecklistItem[] {
  const { standups, today } = data;
  const fmt = (date: string) => fmtTrackerDate(date, today);
  const items: ChecklistItem[] = [];

  items.push(
    standups.enabled
      ? { key: 'standups', ready: true, label: 'Standups on' }
      : {
          key: 'standups',
          ready: false,
          label: 'Standups are off',
          fix: { label: 'Turn on standups', target: 'checkins' },
        },
  );

  // A sprint may start on its first standup day rather than the Monday, as
  // Tue/Thu/Sun standups starting on a Tuesday do.
  const start = standups.startDate;
  const firstStandupDay = standups.days.length ? Math.min(...standups.days) : 1;
  if (!start) {
    items.push({
      key: 'start',
      ready: false,
      label: 'No standup start date',
      fix: { label: 'Set start date', target: 'checkins' },
    });
  } else if (isoWeekday(start) > firstStandupDay) {
    items.push({
      key: 'start',
      ready: false,
      label: `Standups start ${fmt(start)}, after the week's first standup day (${
        SHORT_DAYS[firstStandupDay - 1]
      })`,
      fix: { label: 'Change start date', target: 'checkins' },
    });
  } else {
    items.push({ key: 'start', ready: true, label: `Starts ${fmt(start)}` });
  }

  const end = standups.endDate;
  if (!end) {
    items.push({
      key: 'end',
      ready: false,
      label: 'No standup end date',
      fix: { label: 'Set end date', target: 'checkins' },
    });
  } else if (!start) {
    // The weeks can't be counted without a start; the start item already says so.
    items.push({ key: 'end', ready: true, label: `Ends ${fmt(end)}` });
  } else if (end < start) {
    items.push({
      key: 'end',
      ready: false,
      label: 'End date is before the start date',
      fix: { label: 'Change end date', target: 'checkins' },
    });
  } else {
    const weeks = lastWeekNumber({ startDate: start, endDate: end, sprintSummary: false }) ?? 1;
    items.push({
      key: 'end',
      ready: true,
      label: `${plural(weeks, 'week', 'weeks')}: ${fmt(start)} – ${fmt(end)}`,
    });
  }

  items.push(
    standups.promptCount > 0
      ? {
          key: 'questions',
          ready: true,
          label: plural(standups.promptCount, 'standup question', 'standup questions'),
        }
      : {
          key: 'questions',
          ready: false,
          label: 'No standup questions',
          fix: { label: 'Add questions', target: 'checkins' },
        },
  );
  return items;
}

function lessonsItem(data: ImmersionSprintSetupData): ChecklistItem {
  const fmt = (date: string) => fmtTrackerDate(date, data.today);
  const lessons = publishedModules(data);
  if (!lessons.length) {
    return {
      key: 'lessons',
      ready: false,
      label: 'No lessons yet',
      fix: { label: 'Add lessons', target: 'modules' },
    };
  }
  const undated = lessons.filter((m) => !m.startDay);
  if (undated.length) {
    return {
      key: 'lessons',
      ready: false,
      label: `${plural(undated.length, 'lesson has', 'lessons have')} no release date`,
      fix: { label: 'Schedule lessons', target: 'modules' },
    };
  }
  const weekend = lessons.filter((m) => isoWeekday(m.startDay!) >= 6);
  if (weekend.length) {
    return {
      key: 'lessons',
      ready: false,
      label: `${plural(weekend.length, 'lesson releases', 'lessons release')} on a weekend`,
      detail: weekend
        .slice(0, 3)
        .map((m) => m.title)
        .join(', '),
      fix: { label: 'Check lesson dates', target: 'modules' },
    };
  }
  const days = lessons.map((m) => m.startDay!).sort();
  return {
    key: 'lessons',
    ready: true,
    label: `${plural(lessons.length, 'lesson', 'lessons')} scheduled, ${fmt(days[0])} – ${fmt(
      days[days.length - 1],
    )}`,
  };
}

function gateItems(data: ImmersionSprintSetupData): ChecklistItem[] {
  const { gate, standups, today } = data;
  const fmt = (date: string) => fmtTrackerDate(date, today);
  if (!gate.enabled) {
    return [
      {
        key: 'gate',
        ready: false,
        label: 'Completion gate is off',
        fix: { label: 'Turn on the gate', target: 'completion-gate' },
      },
    ];
  }
  const gated = publishedModules(data).filter((m) => m.isGated).length;
  const items: ChecklistItem[] = [
    gated
      ? {
          key: 'gate',
          ready: true,
          label: `Completion gate on, ${plural(gated, 'lesson', 'lessons')} chosen`,
        }
      : {
          key: 'gate',
          ready: false,
          label: 'No lessons chosen for the completion gate',
          fix: { label: 'Choose lessons', target: 'completion-gate' },
        },
  ];

  items.push(
    gate.autoRemoval
      ? { key: 'auto-removal', ready: true, label: 'Automatic removal on' }
      : {
          key: 'auto-removal',
          ready: false,
          label: 'Automatic removal is off: students who fall behind are warned, not removed',
          fix: { label: 'Turn on removal', target: 'completion-gate' },
        },
  );

  const last = gate.lastEnforcedOn;
  if (!last) {
    items.push({
      key: 'last-removal',
      ready: false,
      label: 'No last removal day: warnings would keep going after the immersion sprint',
      fix: { label: 'Set last removal day', target: 'completion-gate' },
    });
  } else if (
    last < today &&
    (!standups.endDate || (standups.endDate >= today && last < mondayOf(standups.endDate)))
  ) {
    items.push({
      key: 'last-removal',
      ready: false,
      label: `Last removal day ${fmt(last)} has passed`,
      fix: { label: 'Change last removal day', target: 'completion-gate' },
    });
  } else if (standups.startDate && last < standups.startDate) {
    items.push({
      key: 'last-removal',
      ready: false,
      label: `Last removal day ${fmt(last)} is before standups start`,
      fix: { label: 'Change last removal day', target: 'completion-gate' },
    });
  } else if (isoWeekday(last) !== 3) {
    items.push({
      key: 'last-removal',
      ready: false,
      label: `Last removal day ${fmt(last)} isn't a Wednesday`,
      fix: { label: 'Change last removal day', target: 'completion-gate' },
    });
  } else {
    items.push({ key: 'last-removal', ready: true, label: `Last removal day ${fmt(last)}` });
  }
  return items;
}

function acesItem(data: ImmersionSprintSetupData): ChecklistItem {
  return data.acesProgram
    ? { key: 'aces', ready: true, label: `Shown in ACES as ${data.acesProgram}` }
    : {
        key: 'aces',
        ready: false,
        label: 'Not shown in ACES',
        fix: { label: 'Show in ACES', target: 'aces' },
      };
}

/** The launch checklist, in the page's order. */
export function buildChecklist(data: ImmersionSprintSetupData): ChecklistItem[] {
  return [
    ...standupItems(data),
    ...(data.course.accessFormat === 'scheduled' ? [lessonsItem(data)] : []),
    ...gateItems(data),
    ...(data.acesAvailable ? [acesItem(data)] : []),
  ];
}

/** The gate crons' release cutoff: Sunday at 8:00 Pacific. */
const GATE_CUTOFF_TIME = '08:00';

/**
 * The first Sunday whose warning can name a lesson. The Sunday email owes
 * lessons released by Sunday 8:00 Pacific, so a Sunday release at or after
 * 8:00 is first owed the Sunday after. This compares the lesson's naive
 * wall-clock time with 8:00 Pacific, an approximation that ignores the
 * student's time zone.
 */
function firstOwedSunday(module: { startDay?: string; startAt?: string }) {
  if (!module.startDay) {
    return;
  }
  const weekday = isoWeekday(module.startDay);
  if (weekday !== 7) {
    return addDays(module.startDay, 7 - weekday);
  }
  const time = module.startAt?.slice(11, 16) ?? '00:00';
  return time > GATE_CUTOFF_TIME ? addDays(module.startDay, 7) : module.startDay;
}

/**
 * The days the gate can warn or remove on. It follows the gate crons: the
 * Sunday email names lessons released by that Sunday's cutoff, Monday sends a
 * second warning, and Wednesday removes (auto-removal only). Each Sunday and
 * Monday belongs to the Wednesday after it, and both stop once that Wednesday
 * is past the last removal day, as `isWithinGateWindow` decides on the server.
 */
function gateSchedule(data: ImmersionSprintSetupData) {
  const { gate, standups } = data;
  if (!gate.enabled) {
    return;
  }
  const gated = publishedModules(data).filter((m) => m.isGated);
  if (!gated.length) {
    return;
  }
  const fallback = standups.startDate
    ? firstOwedSunday({ startDay: standups.startDate })
    : undefined;
  const firstSunday =
    gated
      .map(firstOwedSunday)
      .filter((d): d is string => !!d)
      .sort()[0] ?? fallback;
  if (!firstSunday) {
    return;
  }
  return { firstSunday, lastEnforcedOn: gate.lastEnforcedOn, autoRemoval: gate.autoRemoval };
}

function marksFor(date: string, schedule: ReturnType<typeof gateSchedule>) {
  if (!schedule || date < schedule.firstSunday) {
    return { warning: false, removal: false };
  }
  const weekday = isoWeekday(date);
  // The Wednesday each day's warning or removal belongs to.
  const wednesday =
    weekday === 7 ? addDays(date, 3) : weekday === 1 ? addDays(date, 2) : weekday === 3 ? date : '';
  if (!wednesday) {
    return { warning: false, removal: false };
  }
  const inWindow = !schedule.lastEnforcedOn || wednesday <= schedule.lastEnforcedOn;
  return {
    warning: inWindow && weekday !== 3,
    removal: inWindow && weekday === 3 && schedule.autoRemoval,
  };
}

function summaryDays(data: ImmersionSprintSetupData) {
  const { standups } = data;
  const days = new Set<string>();
  if (!standups.summariesEnabled) {
    return days;
  }
  const schedule = {
    startDate: standups.startDate,
    endDate: standups.endDate,
    sprintSummary: standups.sprintSummary,
  };
  const last = lastWeekNumber(schedule) ?? 0;
  for (let n = 1; n <= last; n++) {
    const window = summaryWindow(n, schedule);
    if (!window) {
      continue;
    }
    const until = window.dueOn ?? window.closesOn;
    for (let day = window.opensOn; day <= until; day = addDays(day, 1)) {
      days.add(day);
    }
  }
  return days;
}

/** One row per standup week, Monday to Sunday, up to 16. */
export function buildWeekPlan(data: ImmersionSprintSetupData): WeekPlan | undefined {
  const { standups, today } = data;
  if (!standups.startDate || !standups.endDate || standups.endDate < standups.startDate) {
    return;
  }
  const lessonsByDay = new Map<string, string[]>();
  if (data.course.accessFormat === 'scheduled') {
    for (const module of publishedModules(data)) {
      if (!module.startDay) {
        continue;
      }
      lessonsByDay.set(module.startDay, [
        ...(lessonsByDay.get(module.startDay) ?? []),
        module.title,
      ]);
    }
  }
  const schedule = gateSchedule(data);
  const summaries = summaryDays(data);
  const firstWeek = mondayOf(standups.startDate);
  const lastWeek = mondayOf(standups.endDate);
  const weeks: WeekPlanWeek[] = [];
  let weekStart = firstWeek;
  let truncated = false;
  while (weekStart <= lastWeek) {
    if (weeks.length === MAX_WEEK_PLAN_WEEKS) {
      truncated = true;
      break;
    }
    const days: WeekPlanDay[] = [];
    for (let i = 0; i < 7; i++) {
      const date = addDays(weekStart, i);
      days.push({
        date,
        label: fmtShortDate(date),
        lessons: lessonsByDay.get(date) ?? [],
        standup:
          standups.days.includes(i + 1) && date >= standups.startDate && date <= standups.endDate,
        summary: summaries.has(date),
        ...marksFor(date, schedule),
        holiday: holidayOn(date),
        isToday: date === today,
      });
    }
    weeks.push({
      weekNumber: weeks.length + 1,
      weekStart,
      firstLesson: days.find((d) => d.lessons.length)?.lessons[0],
      days,
    });
    weekStart = addDays(weekStart, 7);
  }
  return { weeks, truncated };
}

function standupFacts(data: ImmersionSprintSetupData): SetupFact[] {
  const { standups } = data;
  const summary = standups.summariesEnabled
    ? `Fri – end of Sun${standups.sprintSummary ? ' · final week: immersion sprint summary' : ''}`
    : 'Off';
  const emailsTo =
    standups.sendTo === 'coach'
      ? 'Each student’s coach'
      : standups.notifyEmail || 'Guide and facilitators';
  return [
    { label: 'Status', value: standups.enabled ? 'On' : 'Off' },
    { label: 'Days', value: fmtStandupDays(standups.days) },
    { label: 'Questions', value: String(standups.promptCount) },
    {
      label: 'Reminder',
      value: standups.remindersEnabled
        ? `Daily at ${fmtReminderHour(standups.reminderHour)}`
        : 'Off',
    },
    { label: 'Weekly summary', value: summary },
    { label: 'Emails go to', value: emailsTo },
    { label: 'Default coach', value: standups.defaultCoachName || 'None' },
  ];
}

function deadlineFacts(data: ImmersionSprintSetupData): SetupFact[] {
  const { gate, today } = data;
  const published = publishedModules(data);
  const gated = published.filter((m) => m.isGated).length;
  return [
    { label: 'Status', value: gate.enabled ? 'On' : 'Off' },
    { label: 'Warnings', value: 'Sun and Mon' },
    {
      label: 'Removal',
      value: gate.autoRemoval
        ? `Wed, when ${gate.minDaysMissing}+ days are missing`
        : 'Off: warns only',
    },
    {
      label: 'Last removal day',
      value: gate.lastEnforcedOn ? fmtTrackerDate(gate.lastEnforcedOn, today) : 'Not set',
    },
    { label: 'Lessons gated', value: `${gated} of ${published.length}` },
  ];
}

function trackerFacts(data: ImmersionSprintSetupData): SetupFact[] {
  const { tracker, standups, today } = data;
  const coachAccess = standups.endDate
    ? `Ends ${fmtTrackerDate(coachAccessLastDay(standups.endDate), today)}, the day after standups end`
    : 'Doesn’t end: no standup end date';
  return [
    { label: 'Status', value: tracker.enabled ? 'On' : 'Off' },
    {
      label: 'Start from',
      value: tracker.presetId ? TRACKER_PRESETS[tracker.presetId].label : 'Custom',
    },
    tracker.channelsBy === 'participant'
      ? {
          label: 'Channels',
          value: plural(tracker.channelCount, 'channel family', 'channel families'),
        }
      : {
          label: 'Stages',
          value: `${plural(tracker.stageCount, 'stage', 'stages')}, ${plural(
            tracker.channelCount,
            'channel',
            'channels',
          )}`,
        },
    {
      label: 'Scoreboard',
      value: tracker.scoreboardKind === 'cpl' ? 'Cost per lead' : 'Revenue against a target',
    },
    { label: 'Coach access', value: coachAccess },
  ];
}

export function buildSetupView(data: ImmersionSprintSetupData): SetupView {
  const checklist = buildChecklist(data);
  return {
    checklist,
    readyCount: checklist.filter((item) => item.ready).length,
    weekPlan: buildWeekPlan(data),
    standupFacts: standupFacts(data),
    deadlineFacts: deadlineFacts(data),
    trackerFacts: trackerFacts(data),
  };
}
